/**
 * The tenant's current payment method, and their move-in paperwork (COL-533).
 *
 * Both are READ-ONLY views of data that already exists. Per Ganesh's note in
 * the COL-533 thread — "reuse what exists… restyle only, no new payment logic
 * or backend changes" — nothing here writes, mandates are still granted
 * through `SepaMandateForm`, and the Wohnungsgeberbestätigung is the row the
 * move-in mail already files.
 */

import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

export type TenantPaymentMethod = {
  /** `bookings.preferred_payment_method`, the tenant's own choice. */
  preferred: string | null;
  /** Last four of the mandate's IBAN, when a mandate is active. */
  last4: string | null;
  /** True when an ACTIVE sepa_debit method exists for this tenant. */
  mandateActive: boolean;
  /** Day of the month rent is collected, from `bookings.rent_due_date`. */
  dueDay: number | null;
};

export function useTenantPaymentMethod(booking: any) {
  const { user } = useAuth();
  const bookingId = booking?.id ?? null;

  /*
    The query holds ONLY what has to be fetched: the mandate.

    `preferred` and `dueDay` are columns of the booking the caller already
    has, and used to be baked into this query's result. The key does not
    carry them, so a refetch that raced the booking's own refetch (the
    picker's Save invalidates both at once) cached the OLD preference and
    kept it until the next invalidation -- the tenant had to save twice
    (COL-608). `select` merges them in from the live `booking` on every
    render instead, and keeps React Query's tracked-property subscription
    intact (a spread of the result would opt out of it).
  */
  const preferred = booking?.preferred_payment_method
    ? String(booking.preferred_payment_method)
    : null;
  const dueRaw = Number(booking?.rent_due_date);
  const dueDay = Number.isFinite(dueRaw) && dueRaw > 0 ? dueRaw : null;

  return useQuery<
    Pick<TenantPaymentMethod, "last4" | "mandateActive">,
    Error,
    TenantPaymentMethod
  >({
    queryKey: ["payment-methods", user?.id, "tenant-v2", String(bookingId)],
    enabled: !!user?.id,
    select: (mandate) => ({ ...mandate, preferred, dueDay }),
    queryFn: async () => {
      if (!user?.id) return { last4: null, mandateActive: false };
      const { data, error } = await (supabase as any)
        .from("payment_methods" as any)
        .select("last4,status,type")
        .eq("user_id", user.id)
        .eq("type", "sepa_debit")
        .eq("status", "active")
        .limit(1)
        .maybeSingle();
      // Throw rather than defaulting: an unreadable method must not render as
      // "no mandate", which is what would invite a tenant to set one up twice.
      if (error) throw error;
      return {
        last4: data?.last4 ? String(data.last4) : null,
        mandateActive: !!data,
      };
    },
  });
}

/** The document types the room page can offer for download. */
export const MOVE_IN_DOCUMENT_TYPES = ["wohnungsgeberbestaetigung"] as const;

export type TenantMoveInDocument = {
  id: string;
  documentType: string;
  fileUrl: string | null;
  title: string | null;
};

/**
 * The Wohnungsgeberbestätigung, from the row the move-in mail already files.
 *
 * Ganesh: "we already generate it… saved to the tenant's documents, so V2 can
 * show it from there." Today the tenant only ever receives it by email, so
 * this is the first place in the product they can fetch it themselves.
 */
export function useTenantMoveInDocuments(bookingId?: number | string | null) {
  const { user } = useAuth();

  return useQuery<TenantMoveInDocument[]>({
    queryKey: [
      "tenant-documents",
      user?.id,
      "tenant-v2-move-in",
      String(bookingId ?? ""),
    ],
    enabled: !!user?.id,
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await (supabase as any)
        .from("tenant_documents" as any)
        .select("id,document_type,file_url,title,created_at")
        .eq("user_id", user.id)
        .in("document_type", MOVE_IN_DOCUMENT_TYPES as unknown as string[])
        /*
          Scoped to the booking on screen. Filtering by user alone and taking
          the newest row offered a PREVIOUS tenancy's Wohnungsgeberbestätigung
          as the current room's document — a tenant would have registered at
          the Bürgeramt with their old address. The generator already writes
          `booking_id` (woh-pdf.ts:353).
        */
        .eq("booking_id", bookingId ?? -1)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return ((data as any[]) ?? []).map((row) => ({
        id: String(row.id),
        documentType: String(row.document_type ?? ""),
        fileUrl: row.file_url ? String(row.file_url) : null,
        title: row.title ? String(row.title) : null,
      }));
    },
  });
}
